import React from "react";
import {
  Box,
  Typography,
  Chip,
  alpha,
  useTheme,
  Card,
  CardContent,
  Grid,
} from "@mui/material";
import { LedgerSummaryProps } from "../types";
import { formatCurrency } from "../../../utils";

export const LedgerSummary: React.FC<LedgerSummaryProps> = ({
  selectedAccount,
  summary,
}) => {
  const theme = useTheme();

  if (!selectedAccount) {
    return null;
  }

  return (
    <Box
      sx={{
        p: 3,
        borderBottom: "1px solid",
        borderColor: "divider",
        bgcolor: alpha(theme.palette.primary.main, 0.02),
      }}
    >
      <Grid container spacing={3} alignItems="center">
        <Grid item xs={12} md={4}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {selectedAccount.accountName}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Chip
                label={selectedAccount.accountCode}
                size="small"
                variant="outlined"
              />
              <Chip
                label={selectedAccount.accountType}
                size="small"
                color="primary"
              />
              <Chip
                label={selectedAccount.currency}
                size="small"
                variant="outlined"
              />
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Card
                variant="outlined"
                sx={{
                  height: 80,
                  bgcolor: alpha(theme.palette.info.main, 0.05),
                }}
              >
                <CardContent
                  sx={{
                    p: 1.5,
                    textAlign: "center",
                    "&:last-child": { pb: 1.5 },
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", display: "block" }}
                  >
                    Opening Balance
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: "info.main",
                      fontSize: "1.1rem",
                    }}
                  >
                    {formatCurrency(summary.openingBalance)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card
                variant="outlined"
                sx={{
                  height: 80,
                  bgcolor: alpha(theme.palette.success.main, 0.05),
                }}
              >
                <CardContent
                  sx={{
                    p: 1.5,
                    textAlign: "center",
                    "&:last-child": { pb: 1.5 },
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", display: "block" }}
                  >
                    Total Debits
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: "success.main",
                      fontSize: "1.1rem",
                    }}
                  >
                    {formatCurrency(summary.totalDebits)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card
                variant="outlined"
                sx={{
                  height: 80,
                  bgcolor: alpha(theme.palette.error.main, 0.05),
                }}
              >
                <CardContent
                  sx={{
                    p: 1.5,
                    textAlign: "center",
                    "&:last-child": { pb: 1.5 },
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", display: "block" }}
                  >
                    Total Credits
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: "error.main",
                      fontSize: "1.1rem",
                    }}
                  >
                    {formatCurrency(summary.totalCredits)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card
                variant="outlined"
                sx={{
                  height: 80,
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                }}
              >
                <CardContent
                  sx={{
                    p: 1.5,
                    textAlign: "center",
                    "&:last-child": { pb: 1.5 },
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", display: "block" }}
                  >
                    Closing Balance
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: "primary.main",
                      fontSize: "1.1rem",
                    }}
                  >
                    {formatCurrency(summary.closingBalance)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};
